import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ActivityEntry } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/agent-thread-segments';
import { AgentThinkingIndicator } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thinking-indicator';
import { createStyles } from '@/contexts/theme-context';
import {
  hasToolCallFailed,
  isAwaitingApproval,
  isToolCallRunning,
  toolCallLabel,
  ToolCallPart,
} from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/agent-message-parts';
import { Icons, Typography } from '@usepepper/sprout-kit-ui';
import { Pressable, View } from 'react-native';
import { ThreadMessage } from '@assistant-ui/core';

interface ActivityStep {
  message: ThreadMessage;
  part: ToolCallPart;
}

interface AgentActivityProps {
  entries: ActivityEntry[];
  live: boolean;
}

const stepAccessibilityLabel = (
  label: string,
  failed: boolean,
  awaiting: boolean,
) => {
  if (failed) {
    return i18n.t('sales_portal.superintelligence.step_failed', {
      step: label,
    });
  }
  if (awaiting) {
    return i18n.t('sales_portal.superintelligence.step_awaiting_approval', {
      step: label,
    });
  }
  return label;
};

const AgentActivityStep: React.FC<ActivityStep> = ({ message, part }) => {
  const [styles] = useStyles();
  const label = toolCallLabel(part);
  const failed = hasToolCallFailed(part);
  const awaiting = isAwaitingApproval(message, part);

  return (
    <View
      accessibilityLabel={stepAccessibilityLabel(label, failed, awaiting)}
      accessible
      style={styles.step}
    >
      {failed ? (
        <Icons.AlertCircle color="$iconNegativePrimary" size="$16" />
      ) : null}
      {!failed && awaiting ? (
        <Icons.Hand color="$iconWarningPrimary" size="$16" />
      ) : null}
      <Typography
        color="$textSecondary"
        numberOfLines={2}
        size="$sm"
        style={styles.stepLabel}
      >
        {label}
      </Typography>
    </View>
  );
};

export const AgentActivity: React.FC<AgentActivityProps> = ({
  entries,
  live,
}) => {
  const [styles] = useStyles();
  const steps = entries.flatMap(({ message, part }): ActivityStep[] =>
    part.type === 'tool-call' ? [{ message, part }] : [],
  );
  const [toggled, setToggled] = useState<boolean | null>(null);
  const expanded =
    toggled ??
    steps.some(({ message, part }) => isAwaitingApproval(message, part));
  const running = steps.find(({ message, part }) =>
    isToolCallRunning(message, part),
  );

  const shown =
    live || expanded ? steps.filter((step) => step !== running) : [];
  const failedCount = steps.filter(({ part }) =>
    hasToolCallFailed(part),
  ).length;

  return (
    <View style={styles.container}>
      {live ? (
        <AgentThinkingIndicator
          label={running ? toolCallLabel(running.part) : undefined}
        />
      ) : (
        <Pressable
          accessibilityLabel={i18n.t(
            'sales_portal.superintelligence.activity_toggle',
          )}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          onPress={() => setToggled(!expanded)}
          style={styles.header}
        >
          <Typography color="$textSecondary" size="$sm">
            {steps.length === 1
              ? i18n.t(
                  'sales_portal.superintelligence.activity_summary_singular',
                )
              : i18n.t(
                  'sales_portal.superintelligence.activity_summary_plural',
                  {
                    count: steps.length,
                  },
                )}
          </Typography>
          {failedCount > 0 ? (
            <>
              <Icons.AlertCircle color="$iconNegativePrimary" size="$16" />
              <Typography color="$textSecondary" size="$sm">
                {failedCount === 1
                  ? i18n.t(
                      'sales_portal.superintelligence.activity_failed_singular',
                    )
                  : i18n.t(
                      'sales_portal.superintelligence.activity_failed_plural',
                      {
                        count: failedCount,
                      },
                    )}
              </Typography>
            </>
          ) : null}
          {expanded ? (
            <Icons.ChevronDown color="$iconSecondary" size="$16" />
          ) : (
            <Icons.ChevronRight color="$iconSecondary" size="$16" />
          )}
        </Pressable>
      )}
      {shown.length > 0 ? (
        <View style={styles.steps}>
          {shown.map((step) => (
            <AgentActivityStep key={step.part.toolCallId} {...step} />
          ))}
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignSelf: 'stretch',
    gap: theme.sk.spacing.spaceXsmall,
  },
  header: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  step: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  stepLabel: {
    flexShrink: 1,
  },
  steps: {
    borderLeftColor: theme.sk.color.border.primary,
    borderLeftWidth: 1,
    gap: theme.sk.spacing.spaceXsmall,
    marginLeft: theme.sk.spacing.spaceXsmall,
    paddingLeft: theme.sk.spacing.spaceSmall,
  },
}));
